/** Keep in sync with domain `src/skills/skills-params.util.ts`. No regular expressions. */

export const SKILL_PARAM_MAX = 8;
const SKILL_PARAM_NAME_MAX_LEN = 32;
export const SKILL_ARGUMENTS_PLACEHOLDER = 'arguments';

function isParamNameChar(code: number): boolean {
  return (
    (code >= 97 && code <= 122) ||
    (code >= 48 && code <= 57) ||
    code === 45 ||
    code === 95
  );
}

export function isValidSkillParameterName(name: string): boolean {
  if (name.length < 1 || name.length > SKILL_PARAM_NAME_MAX_LEN) {
    return false;
  }
  if (name.startsWith('-') || name.endsWith('-')) {
    return false;
  }
  for (let i = 0; i < name.length; i++) {
    if (!isParamNameChar(name.charCodeAt(i))) {
      return false;
    }
  }
  return true;
}

type PlaceholderScan = {
  names: string[];
  error: string | null;
};

function scanSkillPlaceholders(prompt: string): PlaceholderScan {
  const text = String(prompt ?? '');
  const names: string[] = [];
  const seen = new Set<string>();
  let i = 0;
  while (i < text.length) {
    if (text[i] === '{' && text[i + 1] === '{') {
      const start = i + 2;
      let end = start;
      while (end < text.length && !(text[end] === '}' && text[end + 1] === '}')) {
        end++;
      }
      if (end >= text.length) {
        return {
          names,
          error: 'Close every placeholder with }}. Example: {{job_code}}.',
        };
      }
      const name = text.slice(start, end);
      if (!isValidSkillParameterName(name)) {
        return {
          names,
          error:
            'Placeholders must look like {{job_code}}: lowercase letters, numbers, hyphens, or underscores.',
        };
      }
      if (!seen.has(name)) {
        seen.add(name);
        names.push(name);
        if (names.length > SKILL_PARAM_MAX) {
          return {
            names,
            error: `A skill can have at most ${SKILL_PARAM_MAX} placeholders.`,
          };
        }
      }
      i = end + 2;
      continue;
    }
    i++;
  }
  return { names, error: null };
}

export function listSkillParameters(prompt: string): string[] {
  return scanSkillPlaceholders(prompt).names;
}

export function skillPromptParameterError(prompt: string): string | null {
  return scanSkillPlaceholders(prompt).error;
}

export function splitSkillArgumentTokens(remainder: string): string[] {
  const text = String(remainder ?? '').trim();
  if (text === '') {
    return [];
  }
  const tokens: string[] = [];
  let current = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === ' ' || ch === '\n' || ch === '\t') {
      if (current !== '') {
        tokens.push(current);
        current = '';
      }
      continue;
    }
    current += ch;
  }
  if (current !== '') {
    tokens.push(current);
  }
  return tokens;
}

export function assignSkillParameterValues(
  parameters: string[],
  remainder: string,
): Record<string, string> {
  const values: Record<string, string> = {};
  const named = parameters.filter((name) => name !== SKILL_ARGUMENTS_PLACEHOLDER);
  const positional: string[] = [];
  for (const token of splitSkillArgumentTokens(remainder)) {
    const eq = token.indexOf('=');
    if (eq > 0) {
      const key = token.slice(0, eq);
      if (parameters.includes(key)) {
        values[key] = token.slice(eq + 1);
        continue;
      }
    }
    positional.push(token);
  }
  let next = 0;
  for (const name of named) {
    if (values[name] != null && values[name] !== '') {
      continue;
    }
    if (next < positional.length) {
      values[name] = positional[next];
      next += 1;
    }
  }
  if (parameters.includes(SKILL_ARGUMENTS_PLACEHOLDER) && values[SKILL_ARGUMENTS_PLACEHOLDER] == null) {
    if (named.length === 0) {
      values[SKILL_ARGUMENTS_PLACEHOLDER] = String(remainder ?? '').trim();
    } else {
      values[SKILL_ARGUMENTS_PLACEHOLDER] = positional.slice(next).join(' ');
    }
  }
  return values;
}

export function missingSkillParameters(
  parameters: string[],
  values: Record<string, string>,
): string[] {
  const missing: string[] = [];
  for (const name of parameters) {
    if (name === SKILL_ARGUMENTS_PLACEHOLDER) {
      continue;
    }
    const value = values[name];
    if (value == null || value.trim() === '') {
      missing.push(name);
    }
  }
  return missing;
}

export function fillSkillPrompt(prompt: string, values: Record<string, string>): string {
  const text = String(prompt ?? '');
  let out = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] === '{' && text[i + 1] === '{') {
      const start = i + 2;
      let end = start;
      while (end < text.length && !(text[end] === '}' && text[end + 1] === '}')) {
        end++;
      }
      if (end >= text.length) {
        out += text.slice(i);
        break;
      }
      const name = text.slice(start, end);
      const value = values[name];
      if (value == null) {
        out += text.slice(i, end + 2);
      } else {
        out += value;
      }
      i = end + 2;
      continue;
    }
    out += text[i];
    i++;
  }
  return out;
}

/** Fields the run dialog must collect. `arguments` is filled from leftover text. */
export function skillParameterFields(parameters: string[]): string[] {
  return parameters.filter((name) => name !== SKILL_ARGUMENTS_PLACEHOLDER);
}

export function skillParameterLabel(name: string): string {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < name.length; i++) {
    const ch = name[i];
    if (ch === '_' || ch === '-') {
      if (current !== '') {
        parts.push(current);
        current = '';
      }
      continue;
    }
    current += ch;
  }
  if (current !== '') {
    parts.push(current);
  }
  return parts.join(' ');
}
